import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { keys } from '../keys'
import type { Pick, Collection, Collaborator } from '@/lib/store/usePickStore'
import type { LinkMetadata } from '@/lib/server/scraper'
import { toast } from 'sonner'
import { mirrorImage } from '@/lib/storage'
import { usePickIntentStore } from '@/lib/store/usePickIntentStore'
import { removeRecentSearchItem } from '@/hooks/useRecentSearches'

interface AddPickVariables {
    metadata?: LinkMetadata
    collectionId?: string | null
    folderId?: string | null
    optimisticId?: string
    url?: string
}

export function useAddPickMutation() {
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationKey: ['addPick'],
        mutationFn: async ({ metadata, collectionId, folderId, optimisticId, url }: { metadata?: LinkMetadata, collectionId?: string | null, folderId?: string | null, optimisticId?: string, url?: string }) => {
            // 🛡️ RELIABILITY: Wait for Auth initialization if not ready
            // This allows the mutation to be called "instantly" from the Share hook
            // while ensuring the server call only fires once we have a session.
            const useAuthStore = (await import('@/lib/store/useAuthStore')).useAuthStore
            let waitCount = 0
            while (!useAuthStore.getState().initialized && waitCount < 50) {
                await new Promise(resolve => setTimeout(resolve, 100))
                waitCount++
            }

            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error("Not authenticated")

            const fallback: LinkMetadata = { url: url || '', title: 'Untitled', description: null, image: null, siteName: null, favicon: null }
            let finalMetadata = metadata || fallback
            let latestTitle = finalMetadata.title || 'Untitled'
            let latestNotes = finalMetadata.description

            if (optimisticId) {
                // Check if the user modified the pick in the UI while we were fetching metadata!
                const picksCache = queryClient.getQueriesData<{ pages: { picks: Pick[] }[] }>({ queryKey: keys.picks.all() })
                for (const [, data] of picksCache) {
                    const found = data?.pages?.flatMap(p => p.picks)?.find(p => p.id === optimisticId)
                    if (found) {
                        if (found.title && found.title !== "Untitled" && found.title !== "Extracting title...") latestTitle = found.title
                        if (found.description !== undefined) latestNotes = found.description
                        break
                    }
                }
            }

            // If metadata is partial, we should try to complete it using the edge function
            if (!finalMetadata.title || !finalMetadata.image) {
                try {
                    const urlToFetch = finalMetadata.url
                    if (urlToFetch) {
                        const { data, error } = await supabase.functions.invoke('fetch-metadata', {
                            body: { url: urlToFetch }
                        })
                        
                        if (error) console.error("Edge function error:", error)
                        if (data) {
                            // Only update empty fields to avoid overwriting user input
                            finalMetadata = { ...finalMetadata, ...data, description: latestNotes || "" }
                            if (latestTitle === 'Untitled') latestTitle = data.title || latestTitle
                        }
                    }
                } catch (err) {
                    console.error("Background fetch in mutation failed:", err)
                }
            }

            // Mirror image if it's temporary (Instagram etc)
            const finalImageUrl = finalMetadata.image ? await mirrorImage(finalMetadata.image) : null

            const { data: pickData, error: pickError } = await supabase
                .from('picks')
                .insert({
                    ...(optimisticId ? { id: optimisticId } : {}),
                    user_id: user.id,
                    url: finalMetadata.url,
                    source: 'web_paste',
                    title: latestTitle,
                    description: latestNotes,
                    image_url: finalImageUrl,
                    image_original_url: finalMetadata.image,
                    author_name: finalMetadata.author,
                    site_name: finalMetadata.siteName,
                    favicon: finalMetadata.favicon,
                    platform: finalMetadata.platform || 'generic',
                    hashtags: finalMetadata.hashtags || [],
                    published_at: finalMetadata.published_at
                })
                .select()
                .single()

            if (pickError) throw pickError

            if (pickData) {
                const { error: membershipError } = await supabase
                    .from('pick_memberships')
                    .insert({
                        user_id: user.id,
                        pick_id: pickData.id,
                        collection_id: collectionId || null,
                        folder_id: folderId || null
                    })
                
                if (membershipError) throw membershipError
            }

            return {
                id: pickData.id,
                url: pickData.url,
                title: pickData.title,
                description: pickData.description,
                image: pickData.image_url,
                siteName: pickData.site_name,
                favicon: pickData.favicon,
                platform: pickData.platform,
                hashtags: pickData.hashtags || [],
                author: pickData.author_name,
                published_at: pickData.published_at,
                createdAt: pickData.created_at,
                collectionId: collectionId || null,
                folderId: folderId || null,
                memberships: [{ collectionId: collectionId || null, folderId: folderId || null, createdAt: new Date().toISOString() }],
                clientId: optimisticId || undefined,
                collection_added_at: new Date().toISOString()
            } as Pick
        },
        onMutate: async ({ metadata, collectionId, folderId, optimisticId, url }: AddPickVariables) => {
            // Signal synchronization progress
            usePickIntentStore.getState().decrementSyncingCount()
            
            // 🔒 Lock background syncs to prevent optimistic UI wipes
            usePickIntentStore.getState().setIsSavingPick(true)

            // 🛡️ CONDITIONAL CANCEL QUERIES:
            // We only cancel queries if optimisticId is present (manual UI save).
            // This prevents the Drawer-close focus event from wiping out the skeleton via background fetch.
            // For background saves (Share Extension), we skip cancellation to prevent cold-start hydration crashes.
            if (optimisticId) {
                await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            }
            
            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())

            const fallback: LinkMetadata = { url: url || '', title: 'Untitled', description: null, image: null, siteName: null, favicon: null }
            const finalMetadata = metadata || fallback

            const hasCompleteData = Boolean(finalMetadata.title && finalMetadata.title !== 'Untitled' && finalMetadata.title !== 'Extracting title...');

            // 🚫 SKELETON SUPPRESSION:
            // If the metadata is incomplete (like from the iOS Share Extension), we SKIP injecting
            // a skeleton into the UI. This prevents ugly cache clobbering during Cold Boots and 
            // relies entirely on the Sonner toast ("Saving 1 link...") for a smooth, unified UX.
            // HOWEVER: If optimisticId is present, it means the user manually tapped "Save" 
            // inside the app's UI. In this case, they EXPECT a skeleton to appear immediately!
            if (!hasCompleteData && !optimisticId) {
                return { previousPicks, previousCollections, previousFolders }
            }

            const optimisticPick: Pick = {
                id: optimisticId || `temp-${Date.now()}`,
                url: finalMetadata.url,
                title: finalMetadata.title || 'Untitled',
                description: finalMetadata.description,
                image: finalMetadata.image,
                siteName: finalMetadata.siteName,
                favicon: finalMetadata.favicon,
                platform: finalMetadata.platform || 'generic',
                hashtags: finalMetadata.hashtags || [],
                author: finalMetadata.author,
                published_at: finalMetadata.published_at,
                createdAt: new Date().toISOString(),
                collectionId: collectionId || null,
                folderId: folderId || null,
                isLoading: !hasCompleteData,
                memberships: [{ collectionId: collectionId || null, folderId: folderId || null, createdAt: new Date().toISOString() }],
                clientId: optimisticId || `temp-${Date.now()}`,
                collection_added_at: new Date().toISOString()
            } as Pick;
            
            ;(optimisticPick as Pick & { isPending?: boolean }).isPending = true;

            // Optimistically insert pick at the beginning of ALL relevant picks lists
            // We use a predicate filter to update every query starting with 'picks' that looks like a paginated list
            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !(old as any).pages) return old
                    return {
                        ...old,
                        pages: old.pages.map((page, index) => {
                            if (index === 0) {
                                return { ...page, picks: [optimisticPick, ...page.picks] }
                            }
                            return page
                        })
                    }
                }
            )

            // Update Counters for collections
            if (collectionId) {
                queryClient.setQueryData<{ collections: any[], collaborators: any }>(
                    keys.collections.all(),
                    (old) => {
                        if (!old) return old
                        const cols = Array.isArray(old) ? old : (old.collections || [])
                        return {
                            ...old,
                            collections: cols.map((c: any) => 
                                c.id === collectionId ? { ...c, count: (c.count || 0) + 1 } : c
                            )
                        }
                    }
                )
            }

            // Update Counters for folders
            if (folderId) {
                queryClient.setQueryData<any[]>(
                    keys.folders.all(),
                    (old) => {
                        if (!old) return old
                        return old.map(f => 
                            f.id === folderId ? { ...f, count: (f.count || 0) + 1 } : f
                        )
                    }
                )
            }

            return { previousPicks, previousCollections, previousFolders, optimisticId: optimisticPick.id }
        },
        onError: (err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            if (context?.previousFolders) queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            console.error('Error adding pick:', err)
            toast.error(`Failed to add pick: ${err.message || 'Unknown error'}`)
        },
        onSuccess: (realPick, _variables, context) => {
            // Replace optimistic pick with real pick in lists
            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !(old as any).pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.map(p => p.id === context?.optimisticId ? realPick : p)
                        }))
                    }
                }
            )
        },
        onSettled: async (_data, _error, variables) => {
            // 🔓 Release the background sync lock before invalidating
            usePickIntentStore.getState().setIsSavingPick(false)

            // 🔄 Step 1: Force EVERY pick-related view to refetch
            // We await these so the toast doesn't disappear before the new data arrives
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: keys.picks.all() }),
                queryClient.invalidateQueries({ queryKey: keys.collections.all() }),
                queryClient.invalidateQueries({ queryKey: keys.folders.all() }),
                queryClient.invalidateQueries({ queryKey: keys.library.status() })
            ])
            
            console.log("🚨 [CACHE DIAGNOSTIC] Invalidation complete for keys:", JSON.stringify(keys.picks.all()));

            // 🧹 Step 2: Cleanup background sync waiting room ONLY after refresh is done
            if (variables.url) {
                const store = usePickIntentStore.getState()
                store.removePendingPick(variables.url)
            }
        },
    })

    return mutation
}

export function useDeletePickMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (id: string) => {
            await supabase.from('pick_memberships').delete().eq('pick_id', id)
            const { error } = await supabase.rpc('soft_delete_pick', { target_pick_id: id })
            if (error) throw error
            removeRecentSearchItem(id)
        },
        onMutate: async (deletedId) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            await queryClient.cancelQueries({ queryKey: keys.folders.all() })

            // Snapshot the previous state
            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())

            // Optimistically update picks: remove from all cached lists
            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !(old as any).pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.filter(p => p.id !== deletedId)
                        }))
                    }
                }
            )

            // To optimistically update collection/folder counters, we'd need to know which collections/folders the pick belonged to.
            // Since we're deleting it globally, and if we just came from a UI where we saw the pick, we can try to find it in the previous picks to see its memberships.
            let deletedPick: Pick | undefined
            for (const [, data] of previousPicks) {
                const pages = (data as any)?.pages
                if (pages) {
                    for (const page of pages) {
                        const found = page.picks.find((p: Pick) => p.id === deletedId)
                        if (found) {
                            deletedPick = found
                            break
                        }
                    }
                }
                if (deletedPick) break
            }

            // Optimistically decrement collection counts
            if (deletedPick?.memberships) {
                const affectedCollectionIds = new Set(deletedPick.memberships.map((m: any) => m.collectionId).filter(Boolean))
                const affectedFolderIds = new Set(deletedPick.memberships.map((m: any) => m.folderId).filter(Boolean))

                // Update Collections
                queryClient.setQueryData<{ collections: any[], collaborators: any }>(
                    keys.collections.all(),
                    (old) => {
                        if (!old) return old
                        return {
                            ...old,
                            collections: old.collections.map(c => 
                                affectedCollectionIds.has(c.id) 
                                    ? { ...c, count: Math.max(0, (c.count || 0) - 1) } 
                                    : c
                            )
                        }
                    }
                )

                // Update Folders
                queryClient.setQueryData<any[]>(
                    keys.folders.all(),
                    (old) => {
                        if (!old) return old
                        return old.map(f => 
                            affectedFolderIds.has(f.id)
                                ? { ...f, count: Math.max(0, (f.count || 0) - 1) } 
                                : f
                        )
                    }
                )
            }

            return { previousPicks, previousCollections, previousFolders }
        },
        onError: (err, _deletedId, context) => {
            // Rollback on error
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) {
                queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            }
            if (context?.previousFolders) {
                queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            }
            console.error('Error deleting pick:', err)
            toast.error(`Failed to delete pick: ${err.message || 'Unknown error'}`)
        },
        onSettled: () => {
            // Always refetch after error or success to ensure true sync
            queryClient.invalidateQueries({ queryKey: keys.picks.all() })
            queryClient.invalidateQueries({ queryKey: keys.collections.all() })
            queryClient.invalidateQueries({ queryKey: keys.folders.all() })
            queryClient.invalidateQueries({ queryKey: keys.library.status() })
        },
    })
}

export function useBulkDeletePicksMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (ids: string[]) => {
            await Promise.all(ids.map(id => 
                supabase.rpc('soft_delete_pick', { target_pick_id: id })
            ))
        },
        onMutate: async (deletedIds) => {
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            await queryClient.cancelQueries({ queryKey: keys.folders.all() })

            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())

            // Find all deleted picks to know which collections/folders to decrement
            const deletedPicks: Pick[] = []
            for (const [, data] of previousPicks) {
                const pages = (data as any)?.pages
                if (pages) {
                    for (const page of pages) {
                        for (const pick of page.picks) {
                            if (deletedIds.includes(pick.id) && !deletedPicks.find(p => p.id === pick.id)) {
                                deletedPicks.push(pick)
                            }
                        }
                    }
                }
            }

            // Optimistically update picks
            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !(old as any).pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.filter(p => !deletedIds.includes(p.id))
                        }))
                    }
                }
            )

            // Optimistically update counters
            const affectedCollectionCounts: Record<string, number> = {}
            const affectedFolderCounts: Record<string, number> = {}

            deletedPicks.forEach(pick => {
                if (pick.memberships) {
                    pick.memberships.forEach((m: any) => {
                        if (m.collectionId) affectedCollectionCounts[m.collectionId] = (affectedCollectionCounts[m.collectionId] || 0) + 1
                        if (m.folderId) affectedFolderCounts[m.folderId] = (affectedFolderCounts[m.folderId] || 0) + 1
                    })
                }
            })

            queryClient.setQueryData<{ collections: any[], collaborators: any }>(
                keys.collections.all(),
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        collections: old.collections.map(c => 
                            affectedCollectionCounts[c.id]
                                ? { ...c, count: Math.max(0, (c.count || 0) - affectedCollectionCounts[c.id]) } 
                                : c
                        )
                    }
                }
            )

            queryClient.setQueryData<any[]>(
                keys.folders.all(),
                (old) => {
                    if (!old) return old
                    return old.map(f => 
                        affectedFolderCounts[f.id]
                            ? { ...f, count: Math.max(0, (f.count || 0) - affectedFolderCounts[f.id]) } 
                            : f
                    )
                }
            )

            return { previousPicks, previousCollections, previousFolders }
        },
        onError: (err, _deletedIds, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            if (context?.previousFolders) queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            console.error('Error bulk deleting picks:', err)
            toast.error(`Failed to delete some picks: ${err.message || 'Unknown error'}`)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: keys.picks.all() })
            queryClient.invalidateQueries({ queryKey: keys.collections.all() })
            queryClient.invalidateQueries({ queryKey: keys.folders.all() })
            queryClient.invalidateQueries({ queryKey: keys.library.status() })
        },
    })
}

export function useMovePickMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ pickId, collectionId, folderId = null, fromCollectionId = undefined }: { pickId: string, collectionId: string | null, folderId?: string | null, fromCollectionId?: string | null }) => {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error("Not authenticated")

            // Delete existing memberships safely
            let deleteQuery = supabase
                .from('pick_memberships')
                .delete()
                .eq('pick_id', pickId)

            if (fromCollectionId !== undefined) {
                if (fromCollectionId === null) {
                    deleteQuery = deleteQuery.is('collection_id', null)
                } else {
                    deleteQuery = deleteQuery.eq('collection_id', fromCollectionId)
                }
            }

            const { error: deleteError } = await deleteQuery

            if (deleteError) throw deleteError

            // Insert new membership if not moving to root (home)
            if (collectionId) {
                const { error: insertError } = await supabase
                    .from('pick_memberships')
                    .insert({
                        user_id: user.id,
                        pick_id: pickId,
                        collection_id: collectionId,
                        folder_id: folderId
                    })
                if (insertError) throw insertError
            }
        },
        onMutate: async ({ pickId, collectionId, folderId }) => {
            // Cancel and snapshot
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            await queryClient.cancelQueries({ queryKey: keys.folders.all() })

            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())

            // Find the pick to know its previous memberships
            let targetPick: Pick | undefined
            for (const [, data] of previousPicks) {
                const pages = (data as any)?.pages
                if (pages) {
                    for (const page of pages) {
                        const found = page.picks.find((p: Pick) => p.id === pickId)
                        if (found) {
                            targetPick = found
                            break
                        }
                    }
                }
                if (targetPick) break
            }

            if (targetPick) {
                const oldMemberships = targetPick.memberships || []
                
                // Track decrements and increments for counters
                const decrements = { collections: [] as string[], folders: [] as string[] }
                const increments = { collections: [] as string[], folders: [] as string[] }

                oldMemberships.forEach((m: any) => {
                    if (m.collectionId) decrements.collections.push(m.collectionId)
                    if (m.folderId) decrements.folders.push(m.folderId)
                })

                if (collectionId) increments.collections.push(collectionId)
                if (folderId) increments.folders.push(folderId)

                // Optimistically update picks across different lists
                // This is extremely complex to do perfectly (e.g. adding it to a new list means we have to prepend it)
                // The easiest and safest optimistic update is to just REMOVE it from lists where it no longer belongs.
                // Prepending it to the new list is harder because we might have pagination, sorting, etc.
                // But we CAN update its memberships in any lists where it currently exists.
                queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                    { queryKey: keys.picks.all() },
                    (old) => {
                        if (!old || !(old as any).pages) return old
                        return {
                            ...old,
                            pages: old.pages.map(page => ({
                                ...page,
                                picks: page.picks.map(p => {
                                    if (p.id === pickId) {
                                        return {
                                            ...p,
                                            collectionId: collectionId || null,
                                            folderId: folderId || null,
                                            memberships: [{ 
                                                collectionId: collectionId || null, 
                                                folderId: folderId || null, 
                                                createdAt: new Date().toISOString() 
                                            }]
                                        }
                                    }
                                    return p
                                })
                            }))
                        }
                    }
                )

                // Update Counters for collections
                queryClient.setQueryData<{ collections: any[], collaborators: any }>(
                    keys.collections.all(),
                    (old) => {
                        if (!old) return old
                        const cols = Array.isArray(old) ? old : (old.collections || [])
                        return {
                            ...old,
                            collections: cols.map(c => {
                                let newCount = c.count || 0
                                if (decrements.collections.includes(c.id)) newCount = Math.max(0, newCount - 1)
                                if (increments.collections.includes(c.id)) newCount += 1
                                return { ...c, count: newCount }
                            })
                        }
                    }
                )

                // Update Counters for folders
                queryClient.setQueryData<any[]>(
                    keys.folders.all(),
                    (old) => {
                        if (!old) return old
                        return old.map(f => {
                            let newCount = f.count || 0
                            if (decrements.folders.includes(f.id)) newCount = Math.max(0, newCount - 1)
                            if (increments.folders.includes(f.id)) newCount += 1
                            return { ...f, count: newCount }
                        })
                    }
                )
            }

            return { previousPicks, previousCollections, previousFolders }
        },
        onError: (err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            if (context?.previousFolders) queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            console.error('Error moving pick:', err)
            toast.error(`Failed to move pick: ${err.message || 'Unknown error'}`)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: keys.picks.all() })
            queryClient.invalidateQueries({ queryKey: keys.collections.all() })
            queryClient.invalidateQueries({ queryKey: keys.folders.all() })
            queryClient.invalidateQueries({ queryKey: keys.library.status() })
        },
    })
}

export function useSyncPicksMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ pickIds, targets }: { pickIds: string[], targets: { collectionId: string | null; folderId: string | null }[] }) => {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error("Not authenticated")

            // 1. Clear existing memberships for these picks
            const { error: deleteError } = await supabase
                .from('pick_memberships')
                .delete()
                .in('pick_id', pickIds)

            if (deleteError) throw deleteError

            // 2. Insert new memberships (if no targets, insert the default Homepage membership)
            const cleanedTargets = targets.filter(t => t.collectionId !== null || t.folderId !== null)
            const finalTargets = cleanedTargets.length > 0 ? cleanedTargets : [{ collectionId: null, folderId: null }]
            
            const inserts = pickIds.flatMap(pickId =>
                finalTargets.map(t => ({
                    user_id: user.id,
                    pick_id: pickId,
                    collection_id: t.collectionId || null,
                    folder_id: t.folderId || null
                }))
            )

            const { error: insertError } = await supabase
                .from('pick_memberships')
                .insert(inserts)

            if (insertError) throw insertError
        },
        onMutate: async ({ pickIds, targets }) => {
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            await queryClient.cancelQueries({ queryKey: keys.folders.all() })

            const previousPicksQueries = queryClient.getQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())

            // Find all affected picks to determine previous memberships
            const affectedPicksMap = new Map<string, Pick>()
            for (const [, data] of previousPicksQueries) {
                const pages = (data as any)?.pages
                if (pages) {
                    for (const page of pages) {
                        for (const pick of page.picks) {
                            if (pickIds.includes(pick.id) && !affectedPicksMap.has(pick.id)) {
                                affectedPicksMap.set(pick.id, JSON.parse(JSON.stringify(pick)))
                            }
                        }
                    }
                }
            }

            const collectionDecrements: Record<string, number> = {}
            const collectionIncrements: Record<string, number> = {}
            const folderDecrements: Record<string, number> = {}
            const folderIncrements: Record<string, number> = {}

            // For each pick, calculate net increments and decrements based on unique IDs
            for (const pickId of pickIds) {
                const oldPick = affectedPicksMap.get(pickId)
                const oldMemberships = oldPick?.memberships || []
                
                const oldCids = new Set(oldMemberships.map((m: any) => m.collectionId).filter((id): id is string => !!id))
                const oldFids = new Set(oldMemberships.map((m: any) => m.folderId).filter((id): id is string => !!id))
                
                const newCids = new Set(targets.map(t => t.collectionId).filter((id): id is string => !!id))
                const newFids = new Set(targets.map(t => t.folderId).filter((id): id is string => !!id))
                
                oldCids.forEach(cid => { if (!newCids.has(cid)) collectionDecrements[cid] = (collectionDecrements[cid] || 0) + 1 })
                newCids.forEach(cid => { if (!oldCids.has(cid)) collectionIncrements[cid] = (collectionIncrements[cid] || 0) + 1 })
                
                oldFids.forEach(fid => { if (!newFids.has(fid)) folderDecrements[fid] = (folderDecrements[fid] || 0) + 1 })
                newFids.forEach(fid => { if (!oldFids.has(fid)) folderIncrements[fid] = (folderIncrements[fid] || 0) + 1 })
            }

            // Optimistically update picks across ALL query contexts (adding and removing dynamically)
            previousPicksQueries.forEach(([queryKey, oldData]) => {
                if (!oldData || !Array.isArray(oldData.pages)) return

                const contextObj = queryKey[1] as any
                const contextStr = contextObj?.context || ''

                const newData = {
                    ...oldData,
                    pages: oldData.pages.map((page, pageIndex) => {
                        let newPicks = page.picks?.map(p => {
                            if (pickIds.includes(p.id)) {
                                const cleanedTargets = targets.filter(t => t.collectionId !== null || t.folderId !== null)
                                const finalTargets = cleanedTargets.length > 0 ? cleanedTargets : [{ collectionId: null, folderId: null }]

                                return {
                                    ...p,
                                    collectionId: finalTargets[0]?.collectionId ?? null,
                                    folderId: finalTargets[0]?.folderId ?? null,
                                    memberships: finalTargets.map(t => ({
                                        collectionId: t.collectionId || null,
                                        folderId: t.folderId || null,
                                        createdAt: new Date().toISOString()
                                    }))
                                }
                            }
                            return p
                        }) || []

                        // Prepend missing picks that were newly added to this context
                        if (pageIndex === 0) {
                            pickIds.forEach(pickId => {
                                const p = affectedPicksMap.get(pickId)
                                if (!p) return
                                const alreadyExists = newPicks.some(existing => existing.id === pickId)
                                if (alreadyExists) return

                                const shouldBeHere = contextStr.startsWith('folder-') 
                                    ? targets.some(t => t.folderId === contextStr.replace('folder-', ''))
                                    : contextStr.startsWith('collection-')
                                        ? targets.some(t => t.collectionId === contextStr.replace('collection-', ''))
                                        : contextStr === 'home' || contextStr === 'unsorted'
                                            ? targets.length === 0 || (targets.length === 1 && !targets[0].collectionId)
                                            : false
                                
                                if (shouldBeHere) {
                                    const cleanedTargets = targets.filter(t => t.collectionId !== null || t.folderId !== null)
                                    const finalTargets = cleanedTargets.length > 0 ? cleanedTargets : [{ collectionId: null, folderId: null }]
                                    const newP = {
                                        ...p,
                                        collectionId: finalTargets[0]?.collectionId ?? null,
                                        folderId: finalTargets[0]?.folderId ?? null,
                                        memberships: finalTargets.map(t => ({
                                            collectionId: t.collectionId || null,
                                            folderId: t.folderId || null,
                                            createdAt: new Date().toISOString()
                                        }))
                                    }
                                    newPicks.unshift(newP)
                                }
                            })
                        }

                        // Remove picks that no longer belong here
                        newPicks = newPicks.filter(p => {
                            if (!pickIds.includes(p.id)) return true
                            
                            if (contextStr.startsWith('folder-')) {
                                const fid = contextStr.replace('folder-', '')
                                return p.memberships?.some((m: any) => m.folderId === fid)
                            } else if (contextStr.startsWith('collection-')) {
                                const cid = contextStr.replace('collection-', '')
                                return p.memberships?.some((m: any) => m.collectionId === cid)
                            } else if (contextStr === 'home' || contextStr === 'unsorted') {
                                return !p.memberships || p.memberships.length === 0 || p.memberships.every((m: any) => !m.collectionId && !m.folderId)
                            }
                            return true
                        })

                        return { ...page, picks: newPicks }
                    })
                }
                queryClient.setQueryData(queryKey, newData)
            })

            // Update Counters for collections
            queryClient.setQueryData<{ collections: any[], collaborators: any }>(
                keys.collections.all(),
                (old) => {
                    if (!old) return old
                    const cols = Array.isArray(old) ? old : (old.collections || [])
                    if (!cols || !Array.isArray(cols)) return old
                    return {
                        ...old,
                        collections: cols.map(c => {
                            let newCount = c.count || 0
                            if (collectionDecrements[c.id]) newCount = Math.max(0, newCount - collectionDecrements[c.id])
                            if (collectionIncrements[c.id]) newCount += collectionIncrements[c.id]
                            return { ...c, count: newCount }
                        })
                    }
                }
            )

            Object.entries(collectionDecrements).forEach(([cId, dec]) => {
                queryClient.setQueryData<any>(keys.collections.detail(cId), (old: any) => {
                    if (!old) return old
                    return { ...old, count: Math.max(0, (old.count || 0) - dec) }
                })
            })
            Object.entries(collectionIncrements).forEach(([cId, inc]) => {
                queryClient.setQueryData<any>(keys.collections.detail(cId), (old: any) => {
                    if (!old) return old
                    return { ...old, count: (old.count || 0) + inc }
                })
            })

            // Update Counters for folders
            queryClient.setQueryData<any[]>(
                keys.folders.all(),
                (old) => {
                    if (!old || !Array.isArray(old)) return old
                    return old.map(f => {
                        let newCount = f.count || 0
                        if (folderDecrements[f.id]) newCount = Math.max(0, newCount - folderDecrements[f.id])
                        if (folderIncrements[f.id]) newCount += folderIncrements[f.id]
                        return { ...f, count: newCount }
                    })
                }
            )

            return { previousPicks: previousPicksQueries, previousCollections, previousFolders }
        },
        onError: (err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            if (context?.previousFolders) queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            console.error('Error syncing picks:', err)
            toast.error(`Failed to assign collections: ${err.message || 'Unknown error'}`)
        },
        onSettled: (_data, _error, { pickIds }) => {
            queryClient.invalidateQueries({ queryKey: keys.picks.all() })
            pickIds.forEach(id => {
                queryClient.invalidateQueries({ queryKey: keys.picks.detail(id) })
            })
            queryClient.invalidateQueries({ queryKey: keys.collections.all() })
            queryClient.invalidateQueries({ queryKey: keys.folders.all() })
            queryClient.invalidateQueries({ queryKey: keys.library.status() })
        },
    })
}

export function useAddExistingPicksMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ pickIds, collectionId, folderId }: { pickIds: string[], collectionId?: string, folderId?: string }) => {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error("Not authenticated")

            const targetCollectionId = collectionId || null
            const targetFolderId = folderId || null

            // Delete any Unsorted (null) memberships so they don't appear in Unsorted anymore
            await supabase
                .from('pick_memberships')
                .delete()
                .in('pick_id', pickIds)
                .is('collection_id', null)
                .is('folder_id', null)

            // PREVENT DUPLICATES WITHIN THE SAME COLLECTION:
            // A pick cannot be in both the root and a folder of the same collection.
            // If we are adding to a specific collection, remove any existing memberships 
            // the picks currently have inside THIS collection before adding them to the new target folder.
            if (targetCollectionId) {
                await supabase
                    .from('pick_memberships')
                    .delete()
                    .in('pick_id', pickIds)
                    .eq('collection_id', targetCollectionId)
            }

            // Get existing memberships strictly to avoid duplicate inserts across other logic
            const { data: currentMemberships } = await supabase
                .from('pick_memberships')
                .select('*')
                .in('pick_id', pickIds)

            const toInsert = pickIds.filter(pid => {
                const exists = currentMemberships?.some(m =>
                    m.pick_id === pid &&
                    m.collection_id === targetCollectionId &&
                    m.folder_id === targetFolderId
                )
                return !exists
            }).map(pid => ({
                user_id: user.id,
                pick_id: pid,
                collection_id: targetCollectionId,
                folder_id: targetFolderId
            }))

            if (toInsert.length > 0) {
                const { error } = await supabase
                    .from('pick_memberships')
                    .insert(toInsert)
                if (error) throw error
            }
        },
        onMutate: async ({ pickIds, collectionId, folderId }) => {
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            await queryClient.cancelQueries({ queryKey: keys.folders.all() })

            const previousPicksQueries = queryClient.getQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData<{ collections: Collection[], collaborators: Record<string, any> }>(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())
            
            const affectedPicksMap = new Map<string, Pick>()
            for (const [, data] of previousPicksQueries) {
                data?.pages?.forEach(page => {
                    page.picks?.forEach(p => {
                        if (pickIds.includes(p.id) && !affectedPicksMap.has(p.id)) {
                            affectedPicksMap.set(p.id, JSON.parse(JSON.stringify(p)))
                        }
                    })
                })
            }

            previousPicksQueries.forEach(([queryKey, oldData]) => {
                if (!oldData || !Array.isArray(oldData.pages)) return
                const contextObj = queryKey[1] as any
                const contextStr = contextObj?.context || ''

                const newData = {
                    ...oldData,
                    pages: oldData.pages.map((page, pageIndex) => {
                        let newPicks = page.picks?.map(p => {
                            if (pickIds.includes(p.id)) {
                                const exists = p.memberships?.some((m: any) =>
                                    m.collectionId === (collectionId || null) &&
                                    m.folderId === (folderId || null)
                                )
                                if (exists) return p

                                return {
                                    ...p,
                                    memberships: [
                                        ...(p.memberships || []).filter((m: any) => m.collectionId !== (collectionId || null)),
                                        {
                                            collectionId: collectionId || null,
                                            folderId: folderId || null,
                                            createdAt: new Date().toISOString()
                                        }
                                    ]
                                }
                            }
                            return p
                        }) || []

                        if (pageIndex === 0) {
                            pickIds.forEach(pickId => {
                                const p = affectedPicksMap.get(pickId)
                                if (!p) return
                                const alreadyExists = newPicks.some(existing => existing.id === pickId)
                                
                                const shouldBeHere = (contextStr.startsWith('folder-') && folderId === contextStr.replace('folder-', ''))
                                    || (contextStr.startsWith('collection-') && collectionId === contextStr.replace('collection-', ''))
                                
                                // If the pick was previously in this context (e.g. root) but is moving out of it (e.g. to a folder), remove it
                                if (alreadyExists && !shouldBeHere) {
                                    // Specifically, if context is a collection root and we are moving to a folder in that collection:
                                    if (contextStr.startsWith('collection-') && collectionId === contextStr.replace('collection-', '') && folderId) {
                                         newPicks = newPicks.filter(existing => existing.id !== pickId)
                                    }
                                }

                                if (!alreadyExists && shouldBeHere) {
                                    const newP = {
                                        ...p,
                                        memberships: [
                                            ...(p.memberships || []).filter((m: any) => m.collectionId !== (collectionId || null)),
                                            {
                                                collectionId: collectionId || null,
                                                folderId: folderId || null,
                                                createdAt: new Date().toISOString()
                                            }
                                        ]
                                    }
                                    newPicks.unshift(newP)
                                }
                            })
                        }
                        
                        newPicks = newPicks.filter(p => {
                            if (!pickIds.includes(p.id)) return true
                            if (contextStr === 'home' || contextStr === 'unsorted') {
                                return !p.memberships || p.memberships.length === 0 || p.memberships.every((m: any) => !m.collectionId && !m.folderId)
                            }
                            return true
                        })

                        return { ...page, picks: newPicks }
                    })
                }
                queryClient.setQueryData(queryKey, newData)
            })

            // Also update the specific usePick(id) detail caches so drawers reopen with fresh optimistic data
            pickIds.forEach(pickId => {
                const detailKey = [...keys.picks.all(), 'detail', pickId]
                const oldDetail = queryClient.getQueryData<any>(detailKey)
                if (oldDetail) {
                    queryClient.setQueryData(detailKey, {
                        ...oldDetail,
                        memberships: [
                            ...(oldDetail.memberships || []).filter((m: any) => m.collectionId !== (collectionId || null)),
                            {
                                collectionId: collectionId || null,
                                folderId: folderId || null,
                                createdAt: new Date().toISOString()
                            }
                        ]
                    })
                }
            })

            const folderIncrements = new Map<string, number>()
            let collectionIncrement = 0

            pickIds.forEach(pickId => {
                const oldPick = affectedPicksMap.get(pickId)
                const alreadyInCollection = oldPick?.memberships?.some((m: any) => m.collectionId === collectionId)
                const alreadyInFolder = oldPick?.memberships?.some((m: any) => m.collectionId === collectionId && m.folderId === folderId)
                
                if (collectionId && !alreadyInCollection) collectionIncrement++
                
                if (folderId && !alreadyInFolder) {
                    folderIncrements.set(folderId, (folderIncrements.get(folderId) || 0) + 1)
                }
                

            })

            if (collectionId && collectionIncrement !== 0) {
                // Update keys.collections.all()
                queryClient.setQueryData<{ collections: Collection[], collaborators: Record<string, Collaborator[]> }>(keys.collections.all(), (old) => {
                    if (!old) return old
                    const cols = old.collections || []
                    return {
                        ...old,
                        collections: cols.map((c: Collection) => c.id === collectionId ? { ...c, count: c.count + collectionIncrement } : c)
                    }
                })
                
                // Update keys.collections.detail(collectionId)
                queryClient.setQueryData<any>(keys.collections.detail(collectionId), (old: any) => {
                    if (!old) return old
                    return {
                        ...old,
                        count: (old.count || 0) + collectionIncrement
                    }
                })
            }

            if (folderIncrements.size > 0) {
                queryClient.setQueryData<any[]>(keys.folders.all(), (old) => {
                    if (!old || !Array.isArray(old)) return old
                    return old.map(f => {
                        const inc = folderIncrements.get(f.id)
                        if (inc !== undefined) {
                            return { ...f, count: Math.max(0, (f.count || 0) + inc) }
                        }
                        return f
                    })
                })
            }

            return { previousPicks: previousPicksQueries, previousCollections, previousFolders }
        },
        onError: (_err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            if (context?.previousFolders) queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            toast.error("Failed to add some picks.")
        },
        onSettled: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: keys.picks.all() }),
                queryClient.invalidateQueries({ queryKey: keys.collections.all() }),
                queryClient.invalidateQueries({ queryKey: keys.folders.all() })
            ])
        }
    })
}

export function useUpdatePickMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (variables: { id: string, updates: Partial<Pick> }) => {
            const { id, updates } = variables
            // Detect if the pick is optimistic (not yet in database)
            const picksCache = queryClient.getQueriesData<{ pages: { picks: Pick[] }[] }>({ queryKey: keys.picks.all() })
            let isOptimistic = false
            for (const [, data] of picksCache) {
                const found = data?.pages?.flatMap(p => p.picks).find(p => p.id === id)
                if (found && (found.isLoading || (found as Pick & { isPending?: boolean }).isPending)) {
                    isOptimistic = true
                    break
                }
            }

            if (isOptimistic) {
                return { id, ...updates, __optimistic: true } as unknown as Pick
            }

            const mappedUpdates: Record<string, string | null> = {}
            if (updates.title !== undefined) mappedUpdates.title = updates.title
            if (updates.description !== undefined) mappedUpdates.description = updates.description
            if (updates.image !== undefined) mappedUpdates.image_url = updates.image
            if (updates.author !== undefined) mappedUpdates.author_name = updates.author
            if (updates.siteName !== undefined) mappedUpdates.site_name = updates.siteName
            if (updates.favicon !== undefined) mappedUpdates.favicon = updates.favicon
            if (updates.platform !== undefined) mappedUpdates.platform = updates.platform
            
            const { data, error } = await supabase
                .from('picks')
                .update(mappedUpdates)
                .eq('id', id)
                .select()
                .single()

            if (error) throw error
            return data
        },
        onMutate: async ({ id, updates }) => {
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })

            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !old.pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.map(p => p.id === id ? { ...p, ...updates } : p)
                        }))
                    }
                }
            )

            const previousDetail = queryClient.getQueryData<Pick>(keys.picks.detail(id))
            if (previousDetail) {
                queryClient.setQueryData(keys.picks.detail(id), { ...previousDetail, ...updates })
            }

            return { previousPicks, previousDetail, id }
        },
        onError: (err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousDetail) {
                queryClient.setQueryData(keys.picks.detail(context.id), context.previousDetail)
            }
            console.error('Error updating pick:', err)
            toast.error(`Failed to update pick: ${err.message || 'Unknown error'}`)
        },
        onSuccess: () => {
            // Success logic if any
        },
        onSettled: (data, __, context) => {
            // DO NOT invalidate if this was an optimistic update, because invalidating asks the DB 
            // for the row which doesn't exist yet, erasing it from our UI cache before the insert completes!
            if (data && (data as Pick & { __optimistic?: boolean }).__optimistic) {
                return
            }

            if (context?.id) {
                queryClient.invalidateQueries({ queryKey: keys.picks.detail(context.id) })
                queryClient.invalidateQueries({ queryKey: keys.picks.all() })
            }
        }
    })
}

export function useTrackVisitPickMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: string) => {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) return
            await supabase.from('pick_visits').upsert({ pick_id: id, user_id: user.id, last_visited_at: new Date().toISOString() })
            await supabase.from('picks').update({ visits: 1 }).eq('id', id)
        },
        onMutate: async (id) => {
            // Cancel outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.picks.detail(id) })

            // Snapshot the previous values
            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })
            const previousDetail = queryClient.getQueryData(keys.picks.detail(id))

            const now = new Date().toISOString()

            // Optimistically update the infinite query cache
            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !old.pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.map(p => p.id === id ? { ...p, lastVisitedAt: now } : p)
                        }))
                    }
                }
            )

            // Optimistically update the detail cache
            if (previousDetail) {
                queryClient.setQueryData(keys.picks.detail(id), {
                    ...(previousDetail as Pick),
                    lastVisitedAt: now
                })
            }

            return { previousPicks, previousDetail }
        },
        onError: (_err, id, context) => {
            // Rollback on error
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousDetail) {
                queryClient.setQueryData(keys.picks.detail(id), context.previousDetail)
            }
        },
        onSettled: () => {
             // We still invalidate to ensure true sync with the server eventually
             queryClient.invalidateQueries({ queryKey: keys.picks.all() })
        }
    })
}

export function useTrackVisitCollectionMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id: string) => {
            await supabase.from('collections').update({ last_visited_at: new Date().toISOString() }).eq('id', id)
        },
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            const previousCollections = queryClient.getQueryData<{ collections: Collection[], collaborators: Record<string, Collaborator[]> }>(keys.collections.all())
            const now = new Date().toISOString()

            if (previousCollections) {
                queryClient.setQueryData(keys.collections.all(), {
                    ...previousCollections,
                    collections: previousCollections.collections.map(c => 
                        c.id === id ? { ...c, lastVisitedAt: now } : c
                    )
                })
            }

            return { previousCollections }
        },
        onError: (_err, _id, context) => {
            if (context?.previousCollections) {
                queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            }
        },
        onSettled: () => {
             queryClient.invalidateQueries({ queryKey: keys.collections.all() })
        }
    })
}

export function useToggleFavouriteMutation() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, isFavourite }: { id: string, isFavourite: boolean }) => {
            const { error } = await supabase.from('picks').update({ is_favourite: isFavourite }).eq('id', id)
            if (error) throw error
        },
        onMutate: async ({ id, isFavourite }) => {
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })

            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !old.pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.map(p => p.id === id ? { ...p, isFavourite } : p)
                        }))
                    }
                }
            )

            const detailKey = [...keys.picks.all(), 'detail', id]
            const previousDetail = queryClient.getQueryData(detailKey)
            if (previousDetail) {
                queryClient.setQueryData(detailKey, { ...(previousDetail as object), isFavourite })
            }

            return { previousPicks, previousDetail, detailKey }
        },
        onError: (_err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousDetail && context?.detailKey) {
                queryClient.setQueryData(context.detailKey, context.previousDetail)
            }
        },
        onSettled: (_data, _error, { id }) => {
             queryClient.invalidateQueries({ queryKey: keys.picks.all() })
             queryClient.invalidateQueries({ queryKey: [...keys.picks.all(), 'detail', id] })
        }
    })
}

export function useRemovePickInstanceMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ pickId, collectionId, folderId }: { pickId: string, collectionId: string | null, folderId?: string | null }) => {
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) throw new Error("Not authenticated")

            let deleteQuery = supabase
                .from('pick_memberships')
                .delete()
                .eq('pick_id', pickId)
                
            if (collectionId) {
                deleteQuery = deleteQuery.eq('collection_id', collectionId)
            } else {
                deleteQuery = deleteQuery.is('collection_id', null)
            }

            if (folderId) {
                deleteQuery = deleteQuery.eq('folder_id', folderId)
            } else {
                deleteQuery = deleteQuery.is('folder_id', null)
            }

            const { error } = await deleteQuery
            if (error) throw error

            // Check if it has any remaining memberships
            const { count, error: countError } = await supabase
                .from('pick_memberships')
                .select('*', { count: 'exact', head: true })
                .eq('pick_id', pickId)
            
            if (countError) throw countError

            // If no memberships left, add it back to the root (homepage/unsorted)
            if (count === 0) {
                const { error: insertError } = await supabase
                    .from('pick_memberships')
                    .insert({
                        user_id: user.id,
                        pick_id: pickId,
                        collection_id: null,
                        folder_id: null
                    })
                if (insertError) throw insertError
            }
        },
        onMutate: async ({ pickId, collectionId, folderId }) => {
            await queryClient.cancelQueries({ queryKey: keys.picks.all() })
            await queryClient.cancelQueries({ queryKey: keys.collections.all() })
            await queryClient.cancelQueries({ queryKey: keys.folders.all() })

            const previousPicks = queryClient.getQueriesData({ queryKey: keys.picks.all() })
            const previousCollections = queryClient.getQueryData(keys.collections.all())
            const previousFolders = queryClient.getQueryData(keys.folders.all())

            queryClient.setQueriesData<{ pages: { picks: Pick[], hasMore: boolean }[] }>(
                { queryKey: keys.picks.all() },
                (old) => {
                    if (!old || !(old as any).pages) return old
                    return {
                        ...old,
                        pages: old.pages.map(page => ({
                            ...page,
                            picks: page.picks.map(p => {
                                if (p.id !== pickId) return p
                                const newMemberships = (p.memberships || []).filter((m: any) => 
                                    !(m.collectionId === (collectionId || null) && m.folderId === (folderId || null))
                                )
                                return { ...p, memberships: newMemberships }
                            }).filter(_p => {
                                // If it has no memberships matching the current context, the list might want to remove it.
                                // It's safer to just let it be, but if we want instant removal from the specific view:
                                // if we're in folder view, and we just removed it from folder, it shouldn't show.
                                // We'll rely on invalidation for UI removal from specific lists.
                                return true
                            })
                        }))
                    }
                }
            )

            if (collectionId) {
                queryClient.setQueryData<{ collections: any[], collaborators: any }>(
                    keys.collections.all(),
                    (old) => {
                        if (!old) return old
                        const cols = Array.isArray(old) ? old : (old.collections || [])
                        return {
                            ...old,
                            collections: cols.map((c: any) => 
                                c.id === collectionId ? { ...c, count: Math.max(0, (c.count || 0) - 1) } : c
                            )
                        }
                    }
                )
            }

            if (folderId) {
                queryClient.setQueryData<any[]>(
                    keys.folders.all(),
                    (old) => {
                        if (!old) return old
                        return old.map(f => 
                            f.id === folderId ? { ...f, count: Math.max(0, (f.count || 0) - 1) } : f
                        )
                    }
                )
            }

            return { previousPicks, previousCollections, previousFolders }
        },
        onError: (err, _variables, context) => {
            if (context?.previousPicks) {
                context.previousPicks.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data)
                })
            }
            if (context?.previousCollections) queryClient.setQueryData(keys.collections.all(), context.previousCollections)
            if (context?.previousFolders) queryClient.setQueryData(keys.folders.all(), context.previousFolders)
            toast.error(`Failed to remove pick: ${err.message || 'Unknown error'}`)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: keys.picks.all() })
            queryClient.invalidateQueries({ queryKey: keys.collections.all() })
            queryClient.invalidateQueries({ queryKey: keys.folders.all() })
            queryClient.invalidateQueries({ queryKey: keys.library.status() })
        },
    })
}
